import { useQuery } from '@tanstack/react-query';
import ApiService from '../services/api.service';

export interface ReportingTreeData {
  nodes: Array<{
    id: string;
    title: string;
  }>;
  edges: Array<{
    from_id: string;
    to_id: string;
    relationship_id: string;
    id: string;
  }>;
}

interface GraphDataResponse {
  data: ReportingTreeData;
}

export function useReportingTree() {
  return useQuery<ReportingTreeData>({
    queryKey: ['reporting-tree'],
    queryFn: async () => {
      const response = await ApiService.getInstance().request(
        'GET',
        '/pipelinenodes/reporting-tree'
      ) as GraphDataResponse;
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get all pipeline node IDs that are connected to a given node in the reporting tree.
 * Traverses both upstream and downstream connections via BFS.
 */
export function getAllConnectedNodeIds(
  nodeId: string,
  edges: ReportingTreeData['edges']
): string[] {
  const fullNodeId = nodeId.startsWith('PipelineNode:') ? nodeId : `PipelineNode:${nodeId}`;
  const visited = new Set<string>();
  const queue = [fullNodeId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);

    // Find all connected nodes (both directions)
    for (const edge of edges) {
      if (edge.from_id === current && !visited.has(edge.to_id)) {
        queue.push(edge.to_id);
      }
      if (edge.to_id === current && !visited.has(edge.from_id)) {
        queue.push(edge.from_id);
      }
    }
  }

  // Return just the pipeline node IDs (without the prefix)
  return Array.from(visited)
    .filter(id => id.startsWith('PipelineNode:'))
    .map(id => id.replace('PipelineNode:', ''));
}

